import type { Project, ProjectUpdateRequest } from '@amarantos/api-client'
import { getGetProjectQueryOptions, useGetProjectSuspense } from '@amarantos/api-client'
import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router'
import { Archive, CircleCheck, CircleSlash, Copy, MoreVertical, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { pick } from 'remeda'
import { ConfirmActionDialog } from '@/components/confirm-action-dialog'
import { PageContent, PageHeader, PageTitle } from '@/components/page'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ProjectReadyBadge } from '@/features/projects/components/project-ready-badge'
import { useEditProjectMutation } from '@/features/projects/hooks/use-project-mutations'
import { WorkStatusUpdateReviewDrawer } from '@/features/work-status-updates/components/work-status-update-review-drawer'
import { workStatusUpdateDrawerSearchSchema } from '@/features/work-status-updates/utils/search'
import { useConfirmAction } from '@/hooks/use-confirm-action'
import { getApiErrorMessage } from '@/utils/errors'
import { formatAddress } from '@/utils/formatters'

// The project page's tabs. "Panel" is the work-status reports feed — the page has no separate
// dashboard; the feed itself is the landing view.
const projectTabs = [
	{
		value: 'reports',
		labelKey: 'projects.detail.tabs.panel',
		to: '/projects/$projectId/reports',
	},
	{
		value: 'networks',
		labelKey: 'projects.detail.tabs.networks',
		to: '/projects/$projectId/networks',
	},
	{
		value: 'benchmarks',
		labelKey: 'projects.detail.tabs.benchmarks',
		to: '/projects/$projectId/benchmarks',
	},
	{
		value: 'materials',
		labelKey: 'projects.detail.tabs.materials',
		to: '/projects/$projectId/materials',
	},
	{
		value: 'supplies',
		labelKey: 'projects.detail.tabs.supplies',
		to: '/projects/$projectId/supplies',
	},
	{
		value: 'documents',
		labelKey: 'projects.detail.tabs.documents',
		to: '/projects/$projectId/documents',
	},
	{
		value: 'persons',
		labelKey: 'projects.detail.tabs.persons',
		to: '/projects/$projectId/persons',
	},
] as const

type ProjectTab = (typeof projectTabs)[number]

export const Route = createFileRoute('/_authenticated/projects/$projectId')({
	validateSearch: workStatusUpdateDrawerSearchSchema,
	params: {
		parse: ({ projectId }) => ({ projectId: Number(projectId) }),
		stringify: ({ projectId }) => ({ projectId: String(projectId) }),
	},
	loader: ({ context: { queryClient }, params: { projectId } }) => {
		queryClient.ensureQueryData(getGetProjectQueryOptions(projectId))
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_project_details' />
	),
	component: ProjectDetailLayoutRoute,
})

type WorkStatusUpdateDrawerState =
	| {
			mode: 'review'
			workStatusUpdateId: number
	  }
	| {
			mode: undefined
			workStatusUpdateId?: undefined
	  }

function ProjectDetailLayoutRoute() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const { projectId } = Route.useParams()
	const navigate = Route.useNavigate()
	const location = useLocation()
	const projectQuery = useGetProjectSuspense(projectId)
	const project = projectQuery.data
	const tabs: readonly ProjectTab[] = projectTabs
	const pathSegment = location.pathname.split('/').filter(Boolean).at(-1)
	const activeTab = tabs.find((tab) => tab.value === pathSegment)?.value
	const drawerState: WorkStatusUpdateDrawerState = pick(search, ['mode', 'workStatusUpdateId'])
	const selectedWorkStatusUpdateId = drawerState.mode === 'review' ? drawerState.workStatusUpdateId : undefined

	const readyToggle = useConfirmAction<boolean, { id: Project['id']; data: ProjectUpdateRequest }>({
		mutation: useEditProjectMutation(),
		toVariables: (ready) => ({ id: projectId, data: { ready } }),
		formatError: (error) => getApiErrorMessage(error, t('projects.detail.ready.error')),
	})

	const pendingReady = readyToggle.target ?? !project.ready

	return (
		<>
			<PageHeader breadcrumbs={[{ label: t('nav.projects'), to: '/projects' }, { label: project.name }]}>
				<PageTitle
					subtitle={formatAddress(project.address)}
					actions={
						<>
							<ProjectReadyBadge ready={project.ready} />
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant='ghost' size='icon-sm' aria-label={t('projects.detail.actions.label')}>
										<MoreVertical />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align='end'>
									<DropdownMenuItem
										variant={project.ready ? 'destructive' : 'default'}
										onSelect={() => readyToggle.request(!project.ready)}
									>
										{project.ready ? <CircleSlash /> : <CircleCheck />}
										{project.ready ? t('projects.detail.ready.unmark') : t('projects.detail.ready.mark')}
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem asChild>
										<Link to='/projects/$projectId/settings' params={{ projectId }}>
											<Settings />
											{t('projects.detail.settings.label')}
										</Link>
									</DropdownMenuItem>
									{/* Placeholders — no handler yet, the actions themselves are still to be built. */}
									<DropdownMenuItem disabled>
										<Copy />
										{t('projects.detail.actions.duplicate')}
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem disabled>
										<Archive />
										{t('projects.detail.actions.archive')}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</>
					}
				>
					{project.name}
				</PageTitle>
				<Tabs value={activeTab}>
					<TabsList>
						{tabs.map((tab) => (
							<TabsTrigger key={tab.to} value={tab.value} asChild>
								<Link to={tab.to} params={{ projectId }}>
									{t(tab.labelKey)}
								</Link>
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			</PageHeader>
			<PageContent>
				<Outlet />
			</PageContent>
			<ConfirmActionDialog
				{...readyToggle.dialogProps}
				confirmVariant={pendingReady ? 'default' : 'destructive'}
				title={pendingReady ? t('projects.detail.ready.mark_title') : t('projects.detail.ready.unmark_title')}
				description={
					pendingReady ? t('projects.detail.ready.mark_description') : t('projects.detail.ready.unmark_description')
				}
				confirmLabel={pendingReady ? t('projects.detail.ready.mark') : t('projects.detail.ready.unmark')}
				pendingLabel={t('projects.detail.ready.pending')}
			/>
			<WorkStatusUpdateReviewDrawer
				workStatusUpdateId={selectedWorkStatusUpdateId}
				onOpenChange={(isOpen) => {
					if (!isOpen) {
						navigate({
							to: location.pathname,
							resetScroll: false,
							search: (previous) => ({
								...previous,
								mode: undefined,
								workStatusUpdateId: undefined,
							}),
						})
					}
				}}
			/>
		</>
	)
}
